import React from 'react';
import ListItem, { ListItemProps } from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import LaunchIcon from '@mui/icons-material/Launch';
import Chip from '@mui/material/Chip';
import { companyTypes, useAuth, policyProps } from '../../context';
import { useAlert, useFetch } from '../../hooks';
import { Alert } from '..';
import {
    PrispekPolicies,
    SpektraPolicies,
    PrismaPolicies
} from '../../constants';

interface ListItemComponentProps extends ListItemProps {
    policy: policyProps;
    company: companyTypes;
    read?: boolean;
}

export const PoliciesListItem: React.FC<ListItemComponentProps> = React.memo(
    ({ policy, read, company, ...otherProps }) => {
        const { session } = useAuth();
        const [post] = useFetch('/dbMemberReadPolicy', 'POST');
        const { alert, dispatchAlert, closeAlert } = useAlert();
        const [currentCompany, setCurrentCompany] =
            React.useState<companyTypes>('PRISPEK');
        const addReadPolicy = React.useCallback(async () => {
            const result = await post({
                data: {
                    member_guid: session.MemberGuid,
                    session_guid: session.SessionGuid,
                    data: policy
                }
            });
            result.success
                ? dispatchAlert({
                      type: 'UPDATE_ALERT',
                      message: `Read ${policy.Name}`,
                      severity: 'success'
                  })
                : dispatchAlert({
                      type: 'UPDATE_ALERT',
                      message: result.msg,
                      severity: 'error'
                  });
        }, [
            dispatchAlert,
            policy,
            post,
            session.MemberGuid,
            session.SessionGuid
        ]);
        React.useEffect(() => {
            PrismaPolicies.forEach(policyConstant =>
                policyConstant.Variant === 'folder' && policyConstant.Policies
                    ? policyConstant.Policies.forEach(
                          subPolicy =>
                              subPolicy.Reference === policy.Reference &&
                              setCurrentCompany('PRISMA')
                      )
                    : policyConstant.Reference === policy.Reference &&
                      setCurrentCompany('PRISMA')
            );
            SpektraPolicies.forEach(policyConstant =>
                policyConstant.Variant === 'folder' && policyConstant.Policies
                    ? policyConstant.Policies.forEach(
                          subPolicy =>
                              subPolicy.Reference === policy.Reference &&
                              setCurrentCompany('SPEKTRA')
                      )
                    : policyConstant.Reference === policy.Reference &&
                      setCurrentCompany('SPEKTRA')
            );
            PrispekPolicies.forEach(policyConstant =>
                policyConstant.Variant === 'folder' && policyConstant.Policies
                    ? policyConstant.Policies.forEach(
                          subPolicy =>
                              subPolicy.Reference === policy.Reference &&
                              setCurrentCompany('PRISPEK')
                      )
                    : policyConstant.Reference === policy.Reference &&
                      setCurrentCompany('PRISPEK')
            );
        }, [policy.Reference]);
        return (
            <>
                <Alert closeHandler={closeAlert} {...alert} />
                <ListItem {...otherProps}>
                    <ListItemText primary={policy.Name} />
                    <ListItemSecondaryAction>
                        <Chip
                            label={currentCompany}
                            size='small'
                            color={
                                currentCompany === 'PRISPEK'
                                    ? 'primary'
                                    : currentCompany === 'SPEKTRA'
                                    ? 'secondary'
                                    : 'default'
                            }
                            variant='outlined'
                        />
                        <Checkbox
                            color='primary'
                            size='small'
                            inputProps={{ 'aria-label': 'primaxry checkbox' }}
                            defaultChecked={policy.Guid !== undefined}
                            disabled={
                                session.Role !== 2 || policy.Guid !== undefined
                            }
                            onChange={addReadPolicy}
                        />
                        <a
                            href={policy.Reference}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <IconButton
                                size='small'
                                edge='end'
                                aria-label='launch'
                            >
                                <LaunchIcon fontSize='small' />
                            </IconButton>
                        </a>
                    </ListItemSecondaryAction>
                </ListItem>
                <Divider />
            </>
        );
    }
);
